import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { aiProviders, schedule as scheduleApi } from "../../api/endpoints";
import ModelBadge from "../../components/ModelBadge.jsx";
import { useSelectedWebsite } from "./Layout.jsx";

// Mirrors RunPromptModal — ordering keeps ChatGPT / Claude / Gemini
// at the top with the "Soon" providers tucked beneath them.
const PROVIDER_ORDER = ["openai", "anthropic", "gemini", "google_ai_overview", "perplexity"];

export default function Schedule() {
  const { t } = useTranslation();
  const { websiteId } = useSelectedWebsite();
  const [exists, setExists] = useState(false);
  const [form, setForm] = useState({
    frequency: 4,
    ai_models: ["openai", "anthropic", "gemini"],
    is_active: true,
  });
  const [providers, setProviders] = useState([]); // [{value,label,configured}]
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    aiProviders
      .list()
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        list.sort(
          (a, b) =>
            PROVIDER_ORDER.indexOf(a.value) - PROVIDER_ORDER.indexOf(b.value),
        );
        setProviders(list);
      })
      .catch(() => {
        setProviders([
          { value: "openai", label: "ChatGPT", configured: true },
          { value: "anthropic", label: "Claude", configured: true },
          { value: "gemini", label: "Gemini", configured: true },
        ]);
      });
  }, []);

  useEffect(() => {
    if (!websiteId) return;
    scheduleApi.get(websiteId).then((r) => {
      const has = r.data && r.data.frequency !== undefined;
      setExists(Boolean(has));
      if (has) setForm({ ...form, ...r.data });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  if (!websiteId)
    return <div className="text-slate-500">{t("prompts.pickWebsite")}</div>;

  function toggleModel(m, configured) {
    if (!configured) return;
    setForm((s) => ({
      ...s,
      ai_models: s.ai_models.includes(m)
        ? s.ai_models.filter((x) => x !== m)
        : [...s.ai_models, m],
    }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Strip any "Soon" providers from the payload defensively — the
      // backend would reject them with a 400, but a clearer client-side
      // filter avoids a confusing round-trip when the saved schedule
      // contains a model that later lost its key.
      const configuredKeys = new Set(
        providers.filter((p) => p.configured).map((p) => p.value),
      );
      const cleanedModels = (form.ai_models || []).filter((m) =>
        configuredKeys.size ? configuredKeys.has(m) : true,
      );
      const payload = {
        frequency: Number(form.frequency),
        ai_models: cleanedModels,
        is_active: form.is_active,
      };
      if (exists) await scheduleApi.update(websiteId, payload);
      else await scheduleApi.create(websiteId, payload);
      setExists(true);
      alert(t("schedule.saved"));
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          JSON.stringify(err?.response?.data) ||
          t("schedule.couldNotSave"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-8 max-w-xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{t("schedule.title")}</h1>
        <p className="text-sm text-slate-600">{t("schedule.subtitle")}</p>
      </div>

      <form onSubmit={onSubmit} className="card space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700">
            {t("schedule.scansPerMonth")}: <span className="mono">{form.frequency}</span>
          </label>
          <input
            type="range"
            min={1}
            max={31}
            value={form.frequency}
            onChange={(e) =>
              setForm({ ...form, frequency: Number(e.target.value) })
            }
            className="w-full mt-2"
          />
        </div>

        <div>
          <div className="text-sm font-medium text-slate-700 mb-2">
            {t("schedule.models")}
          </div>
          <div className="flex flex-wrap gap-2">
            {providers.map((p) => {
              const configured = Boolean(p.configured);
              const on = form.ai_models.includes(p.value);
              return (
                <label
                  key={p.value}
                  className={[
                    "flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 transition",
                    !configured
                      ? "bg-slate-50 text-slate-400 ring-slate-200 cursor-not-allowed"
                      : on
                        ? "ring-brand-500 bg-brand-50 cursor-pointer"
                        : "bg-white text-slate-700 ring-slate-300 cursor-pointer",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                    checked={on}
                    disabled={!configured}
                    onChange={() => toggleModel(p.value, configured)}
                  />
                  <ModelBadge modelKey={p.value} size="sm" />
                  {!configured && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100 ring-1 ring-amber-200 rounded-full px-1.5 py-0.5">
                      {t("models.soon")}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          {t("schedule.active")}
        </label>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <button className="btn-primary" disabled={busy}>
          {busy
            ? t("schedule.saving")
            : exists
              ? t("schedule.update")
              : t("schedule.create")}
        </button>
      </form>
    </div>
  );
}
